import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { socialAPI } from '../../utils/api';

// Async thunk для создания группы
export const createGroup = createAsyncThunk(
  'groups/createGroup',
  async (groupData, { rejectWithValue }) => {
    try {
      const response = await socialAPI.createGroup(groupData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create group');
    }
  }
);

// Async thunk для получения групп пользователя
export const fetchUserGroups = createAsyncThunk(
  'groups/fetchUserGroups',
  async ({ limit = 20, offset = 0 } = {}, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getUserGroups({ limit, offset });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch user groups');
    }
  }
);

// Async thunk для получения детальной информации о группе
export const fetchGroupDetails = createAsyncThunk(
  'groups/fetchGroupDetails',
  async (groupId, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getGroupDetails(groupId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch group details');
    }
  }
);

// Async thunk для присоединения к группе
export const joinGroup = createAsyncThunk(
  'groups/joinGroup',
  async (groupId, { rejectWithValue }) => {
    try {
      const response = await socialAPI.joinGroup(groupId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to join group');
    }
  }
);

// Async thunk для выхода из группы
export const leaveGroup = createAsyncThunk(
  'groups/leaveGroup',
  async (groupId, { rejectWithValue }) => {
    try {
      const response = await socialAPI.leaveGroup(groupId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to leave group');
    }
  }
);

// Async thunk для приглашения пользователя в группу
export const inviteToGroup = createAsyncThunk(
  'groups/inviteToGroup',
  async ({ groupId, userId }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.inviteToGroup(groupId, userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to invite to group');
    }
  }
);

// Async thunk для удаления пользователя из группы
export const removeFromGroup = createAsyncThunk(
  'groups/removeFromGroup',
  async ({ groupId, userId }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.removeFromGroup(groupId, userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to remove from group');
    }
  }
);

// Async thunk для обновления группы
export const updateGroup = createAsyncThunk(
  'groups/updateGroup',
  async ({ groupId, groupData }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.updateGroup(groupId, groupData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update group');
    }
  }
);

// Async thunk для получения участников группы
export const fetchGroupMembers = createAsyncThunk(
  'groups/fetchGroupMembers',
  async ({ groupId, limit = 20, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getGroupMembers(groupId, { limit, offset });
      return { groupId, members: response.data, hasMore: response.data.length === limit };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch group members');
    }
  }
);

const groupsSlice = createSlice({
  name: 'groups',
  initialState: {
    // Список групп пользователя
    userGroups: [],
    // Детальная информация о группах: { groupId: groupDetails }
    groupDetails: {},
    // Участники групп: { groupId: { members: [], hasMore: boolean, isLoading: boolean } }
    groupMembers: {},
    // Статус участия в группах: { groupId: 'none' | 'member' | 'admin' | 'owner' }
    membershipStatus: {},
    // Общее состояние
    isLoading: false,
    error: null,
    hasMoreGroups: true,
  },
  reducers: {
    // Обновление статуса участия (приходит через WebSocket)
    updateMembershipStatus: (state, action) => {
      const { groupId, status } = action.payload;
      state.membershipStatus[groupId] = status;
    },

    // Добавление нового участника (приходит через WebSocket)
    addGroupMember: (state, action) => {
      const { groupId, member } = action.payload;
      
      if (!state.groupMembers[groupId]) {
        state.groupMembers[groupId] = { members: [], hasMore: true, isLoading: false };
      }
      
      // Проверяем, нет ли уже такого участника
      const exists = state.groupMembers[groupId].members.find(m => m.id === member.id);
      if (!exists) {
        state.groupMembers[groupId].members.push(member);
      }
    },

    // Удаление участника (приходит через WebSocket)
    removeGroupMember: (state, action) => {
      const { groupId, userId } = action.payload;
      
      if (state.groupMembers[groupId]) {
        state.groupMembers[groupId].members = state.groupMembers[groupId].members.filter(
          m => m.id !== userId
        );
      }
    },

    // Обновление информации о группе (приходит через WebSocket)
    updateGroupInfo: (state, action) => {
      const group = action.payload;
      
      // Обновляем в списке групп пользователя
      const groupIndex = state.userGroups.findIndex(g => g.id === group.id);
      if (groupIndex !== -1) {
        state.userGroups[groupIndex] = { ...state.userGroups[groupIndex], ...group };
      }
      
      // Обновляем детальную информацию
      if (state.groupDetails[group.id]) {
        state.groupDetails[group.id] = { ...state.groupDetails[group.id], ...group };
      }
    },

    // Добавление новой группы (приходит через WebSocket)
    addGroup: (state, action) => {
      const group = action.payload;
      
      // Проверяем, нет ли уже такой группы
      const exists = state.userGroups.find(g => g.id === group.id);
      if (!exists) {
        state.userGroups.unshift(group);
        state.membershipStatus[group.id] = 'member';
      }
    },

    // Удаление группы (приходит через WebSocket)
    removeGroup: (state, action) => {
      const groupId = action.payload;
      
      // Удаляем из списка групп
      state.userGroups = state.userGroups.filter(g => g.id !== groupId);
      
      // Удаляем детальную информацию
      delete state.groupDetails[groupId];
      
      // Удаляем участников
      delete state.groupMembers[groupId];
      
      // Удаляем статус участия
      delete state.membershipStatus[groupId];
    },

    // Оптимистичное обновление статуса участия
    updateMembershipStatusOptimistic: (state, action) => {
      const { groupId, status } = action.payload;
      state.membershipStatus[groupId] = status;
    },

    // Очистка ошибки
    clearError: (state) => {
      state.error = null;
    },

    // Сброс состояния
    resetGroupsState: (state) => {
      state.userGroups = [];
      state.groupDetails = {};
      state.groupMembers = {};
      state.membershipStatus = {};
      state.isLoading = false;
      state.error = null;
      state.hasMoreGroups = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Создание группы
      .addCase(createGroup.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createGroup.fulfilled, (state, action) => {
        state.isLoading = false;
        const group = action.payload;
        
        // Добавляем в список групп
        state.userGroups.unshift(group);
        state.membershipStatus[group.id] = 'owner';
        
        // Инициализируем участников
        state.groupMembers[group.id] = { members: [], hasMore: true, isLoading: false };
      })
      .addCase(createGroup.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Получение групп пользователя
      .addCase(fetchUserGroups.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserGroups.fulfilled, (state, action) => {
        state.isLoading = false;
        const { groups, hasMore } = action.payload;
        
        // Если это первая загрузка, заменяем список
        if (state.userGroups.length === 0) {
          state.userGroups = groups;
        } else {
          // Иначе добавляем новые группы
          const existingIds = new Set(state.userGroups.map(g => g.id));
          const newGroups = groups.filter(g => !existingIds.has(g.id));
          state.userGroups = [...state.userGroups, ...newGroups];
        }
        
        state.hasMoreGroups = hasMore;
        
        // Обновляем статусы участия
        groups.forEach(group => {
          state.membershipStatus[group.id] = group.membershipStatus || 'member';
        });
      })
      .addCase(fetchUserGroups.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Получение детальной информации о группе
      .addCase(fetchGroupDetails.pending, (state, action) => {
        const groupId = action.meta.arg;
        state.groupDetails[groupId] = { ...state.groupDetails[groupId], isLoading: true };
      })
      .addCase(fetchGroupDetails.fulfilled, (state, action) => {
        const group = action.payload;
        state.groupDetails[group.id] = { ...group, isLoading: false };
      })
      .addCase(fetchGroupDetails.rejected, (state, action) => {
        const groupId = action.meta.arg;
        if (state.groupDetails[groupId]) {
          state.groupDetails[groupId].isLoading = false;
        }
        state.error = action.payload;
      })
      
      // Присоединение к группе
      .addCase(joinGroup.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(joinGroup.fulfilled, (state, action) => {
        state.isLoading = false;
        const { groupId } = action.payload;
        state.membershipStatus[groupId] = 'member';
      })
      .addCase(joinGroup.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Выход из группы
      .addCase(leaveGroup.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(leaveGroup.fulfilled, (state, action) => {
        state.isLoading = false;
        const { groupId } = action.payload;
        state.membershipStatus[groupId] = 'none';
      })
      .addCase(leaveGroup.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Приглашение в группу
      .addCase(inviteToGroup.fulfilled, (state, action) => {
        const { groupId, member } = action.payload;
        
        if (!state.groupMembers[groupId]) {
          state.groupMembers[groupId] = { members: [], hasMore: true, isLoading: false };
        }
        
        const exists = state.groupMembers[groupId].members.find(m => m.id === member.id);
        if (!exists) {
          state.groupMembers[groupId].members.push(member);
        }
      })
      .addCase(inviteToGroup.rejected, (state, action) => {
        console.error('Failed to invite to group:', action.payload);
      })
      
      // Удаление из группы
      .addCase(removeFromGroup.fulfilled, (state, action) => {
        const { groupId, userId } = action.payload;
        
        if (state.groupMembers[groupId]) {
          state.groupMembers[groupId].members = state.groupMembers[groupId].members.filter(
            m => m.id !== userId
          );
        }
      })
      .addCase(removeFromGroup.rejected, (state, action) => {
        console.error('Failed to remove from group:', action.payload);
      })
      
      // Обновление группы
      .addCase(updateGroup.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateGroup.fulfilled, (state, action) => {
        state.isLoading = false;
        const group = action.payload;
        
        // Обновляем в списке групп
        const groupIndex = state.userGroups.findIndex(g => g.id === group.id);
        if (groupIndex !== -1) {
          state.userGroups[groupIndex] = { ...state.userGroups[groupIndex], ...group };
        }
        
        // Обновляем детальную информацию
        state.groupDetails[group.id] = { ...state.groupDetails[group.id], ...group };
      })
      .addCase(updateGroup.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Получение участников группы
      .addCase(fetchGroupMembers.pending, (state, action) => {
        const { groupId } = action.meta.arg;
        
        if (!state.groupMembers[groupId]) {
          state.groupMembers[groupId] = { members: [], hasMore: true, isLoading: false };
        }
        state.groupMembers[groupId].isLoading = true;
      })
      .addCase(fetchGroupMembers.fulfilled, (state, action) => {
        const { groupId, members, hasMore } = action.payload;
        
        state.groupMembers[groupId] = { members, hasMore, isLoading: false };
      })
      .addCase(fetchGroupMembers.rejected, (state, action) => {
        const { groupId } = action.meta.arg;
        
        if (state.groupMembers[groupId]) {
          state.groupMembers[groupId].isLoading = false;
        }
        state.error = action.payload;
      });
  },
});

// Экспортируем actions
export const {
  updateMembershipStatus,
  addGroupMember,
  removeGroupMember,
  updateGroupInfo,
  addGroup,
  removeGroup,
  updateMembershipStatusOptimistic,
  clearError,
  resetGroupsState,
} = groupsSlice.actions;

// Селекторы
export const selectUserGroups = (state) => state.groups.userGroups;
export const selectGroupDetails = (state, groupId) => state.groups.groupDetails[groupId] || null;
export const selectGroupMembers = (state, groupId) => {
  return state.groups.groupMembers[groupId] || { members: [], hasMore: true, isLoading: false };
};
export const selectMembershipStatus = (state, groupId) => state.groups.membershipStatus[groupId] || 'none';
export const selectGroupsLoading = (state) => state.groups.isLoading;
export const selectGroupsError = (state) => state.groups.error;
export const selectHasMoreGroups = (state) => state.groups.hasMoreGroups;

export default groupsSlice.reducer;
